import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Copy, Inbox as InboxIcon, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { api, fmtDate } from "@/lib/api";
import { toast } from "sonner";

const URG = { alta: "bg-red-50 text-red-700", media: "bg-amber-50 text-amber-700", bassa: "bg-emerald-50 text-emerald-700" };

export const Alerts = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/alerts").then(({ data }) => setItems(data));
    api.post("/alerts/read").then(() => window.dispatchEvent(new Event("bz:deadlines")));
  }, []);
  return (
    <div data-testid="alerts-page">
      <PageHeader eyebrow="Avvisi" title="Cosa è successo" desc="Lettere fotografate dai tuoi familiari, email analizzate e altre novità." />
      {items.length === 0 && <p className="rounded-3xl border bg-white p-10 text-slate-500" data-testid="alerts-empty">Nessun avviso.</p>}
      <div className="space-y-4">
        {items.map((a) => {
          const r = a.data?.result;
          return (
            <div key={a.alert_id} className="rounded-3xl border bg-white p-6" data-testid={`alert-${a.alert_id}`}>
              <div className="flex flex-wrap items-center gap-2"><Bell className="h-4 w-4 text-[#E07A5F]" /><p className="font-medium">{a.title}</p>{a.data?.urgenza && <span className={`text-xs rounded-full px-2 py-0.5 ${URG[a.data.urgenza] || URG.media}`}>Urgenza {a.data.urgenza}</span>}<span className="text-xs text-slate-400 ml-auto">{fmtDate(a.created_at)}</span></div>
              <p className="mt-2 text-slate-700">{a.body}</p>
              {r?.importo && <p className="mt-2 font-medium text-[#1E3A8A]">Importo: {r.importo}</p>}
              {r?.cosa_fare?.length > 0 && <ol className="mt-3 list-decimal pl-5 text-sm text-slate-700 space-y-1">{r.cosa_fare.map((s) => <li key={s}>{s}</li>)}</ol>}
              {a.link && a.link !== "/app/avvisi" && <Link to={a.link} className="mt-3 inline-block text-sm text-[#1E3A8A]">Apri</Link>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function Inbox() {
  const [addr, setAddr] = useState(null);
  const [mails, setMails] = useState([]);
  useEffect(() => {
    api.get("/inbox/address").then(({ data }) => setAddr(data));
    api.get("/inbox").then(({ data }) => setMails(data));
  }, []);
  if (!addr) return <div className="h-40 rounded-3xl bg-slate-100 animate-pulse" />;

  return (
    <div data-testid="inbox-page">
      <PageHeader eyebrow="Inoltro email" title="Email in arrivo" desc="Inoltra a Burocrazy bollette, PEC e comunicazioni di INPS, banca o Comune: le leggiamo, archiviamo gli allegati e creiamo le scadenze." />
      <div className="rounded-3xl bg-[#0B0F19] text-white p-8 noise relative overflow-hidden">
        <Mail className="h-8 w-8 text-[#E07A5F]" />
        {addr.enabled ? (
          <>
            <p className="mt-4 text-sm text-slate-300">Il tuo indirizzo personale</p>
            <div className="mt-2 flex flex-col sm:flex-row gap-2 max-w-2xl">
              <Input readOnly value={addr.address} className="h-12 bg-white/10 border-white/20 text-white" data-testid="inbox-address" />
              <Button onClick={() => { navigator.clipboard.writeText(addr.address); toast.success("Indirizzo copiato"); }} className="h-12 rounded-full bg-[#E07A5F] hover:bg-[#d06a50]" data-testid="inbox-copy-btn"><Copy className="h-4 w-4 mr-2" /> Copia</Button>
            </div>
            <p className="mt-4 text-sm text-slate-300">Consiglio: imposta nella tua casella una regola di inoltro automatico per le email di enti e fornitori.</p>
          </>
        ) : (
          <p className="mt-4 text-lg" data-testid="inbox-disabled">La ricezione email è in fase di attivazione. A breve troverai qui il tuo indirizzo personale.</p>
        )}
      </div>
      <div className="mt-6 rounded-3xl border bg-white p-6">
        <h3 className="font-medium flex items-center gap-2"><InboxIcon className="h-4 w-4" /> Ricevute</h3>
        {mails.length === 0 && <p className="mt-2 text-sm text-slate-500">Nessuna email ricevuta.</p>}
        {mails.map((m) => (
          <div key={m.resend_email_id} className="py-3 border-b last:border-0" data-testid={`inbox-mail-${m.resend_email_id}`}>
            <p className="font-medium">{m.subject || "(senza oggetto)"}</p>
            <p className="text-xs text-slate-500">{m.from} · {fmtDate(m.created_at)} · {m.status === "processed" ? `${(m.analysis_ids || []).length} analisi` : m.status}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
